/**
 * Email Service
 * Sends emails via Cloud Function (Hostinger SMTP backend) to avoid CORS issues
 * Falls back to Firestore mail collection if Cloud Function fails
 */

import { collection, addDoc, query, where, getDocs } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../firebaseConfig';

interface SendEmailOptions {
    to: string;
    subject: string;
    html: string;
    customerId?: string;  // Optional: link email to customer for tracking
    category?: string;    // Optional: categorize the email type
}

interface EmailTrackingResult {
    success: boolean;
    messageId?: string;
    emailRecordId?: string;
    error?: string;
}

export const emailService = {
    /**
     * Send an email via SMTP Cloud Function (avoids CORS)
     * Falls back to Firestore mail collection if function fails
     */
    sendEmail: async (options: SendEmailOptions): Promise<EmailTrackingResult> => {
        const { to, subject, html, customerId, category } = options;

        try {
            // Try sending via Cloud Function (uses Hostinger SMTP on backend)
            const sendEmailFn = httpsCallable(functions, 'dispatchSystemEmail');
            const result = await sendEmailFn({
                to,
                subject,
                html,
                customerId,
                category
            });

            const data = result.data as { success: boolean; messageId?: string; emailRecordId?: string };

            console.log('✅ Email sent via SMTP Cloud Function to:', to);

            return {
                success: true,
                messageId: data.messageId,
                emailRecordId: data.emailRecordId
            };

        } catch (error: any) {
            console.error('❌ Cloud Function email failed:', error.message);
            console.log('📧 Falling back to Firestore mail collection...');

            // Fallback: Add to Firestore mail collection (processed by Firebase Extension)
            try {
                const mailDoc = await addDoc(collection(db, 'mail'), {
                    to: [to],
                    message: { subject, html },
                    delivery: { attempts: 0, state: 'PENDING' },
                    createdAt: new Date().toISOString(),
                    customerId: customerId || null,
                    category: category || 'general'
                });

                // Also log to email tracking
                await addDoc(collection(db, 'emailTracking'), {
                    to: to,
                    subject: subject,
                    customerId: customerId || null,
                    category: category || 'general',
                    status: 'SENT',
                    sentAt: new Date().toISOString(),
                    provider: 'firestore-mail-fallback',
                    mailDocId: mailDoc.id
                });

                // Note: Activity logging is handled by the caller (e.g., sendLegalNoticeCustom)
                // to avoid duplicate entries

                console.log('✅ Email sent via Firestore mail collection fallback');

                return {
                    success: true,
                    messageId: mailDoc.id
                };

            } catch (fallbackError: any) {
                console.error('❌ Firestore mail fallback also failed:', fallbackError);

                // Log failure if customerId provided
                if (customerId) {
                    try {
                        await addDoc(collection(db, 'activityLog'), {
                            customerId: customerId,
                            type: 'EMAIL_FAILED',
                            timestamp: new Date().toISOString(),
                            actor: 'system',
                            details: {
                                subject: subject,
                                to: to,
                                error: fallbackError.message || 'Unknown error',
                                originalError: error.message
                            }
                        });
                    } catch (logError) {
                        console.error('Failed to log email failure:', logError);
                    }
                }

                return {
                    success: false,
                    error: fallbackError.message || 'Failed to send email'
                };
            }
        }
    },

    /**
     * Get email tracking history for a customer
     */
    getCustomerEmailHistory: async (customerId: string): Promise<any[]> => {
        try {
            const q = query(
                collection(db, 'emailTracking'),
                where('customerId', '==', customerId)
            );
            const snapshot = await getDocs(q);
            return snapshot.docs.map(d => ({ id: d.id, ...d.data() }))
                .sort((a: any, b: any) => new Date(b.sentAt).getTime() - new Date(a.sentAt).getTime());
        } catch (error) {
            console.error('Error fetching email history:', error);
            return [];
        }
    }
};

export default emailService;
